// src/pages/ClientProfilePage.jsx
// ============================================================
// CLIENT PROFILE PAGE - EA FOCUS MODE + MVP SIMPLIFICATION
// 
// REFACTORED (Jan 2026):
// - Removed tabs and Generated Summary (MVP)
// - Removed breadcrumb from EA banner
// - Progress bars use brand orange (#FF6E4C)
// - Sections collapse by default (except key ones)
// - Missing fields are clickable (scroll + focus + highlight)
// - Helper text under key sections
// - Chrome autofill blocking
// - UPDATED: Uses unified ProfileStrengthSidebar component
// ============================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AlertCircle, Building2, Users, MapPin, Save, RefreshCw, 
  Check, X, Hash, Edit2, Activity, TrendingUp, Globe, ChevronDown, ChevronUp
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { 
  runGeneration, getPreview, getAuditHistory, 
  getRunStatus, cancelRun, validateNpi as validateNpiAPI, 
  validateAddress as validateAddressAPI, getOrgDefaults 
} from '../services/clientProfileService';

// ✅ NEW: Import unified ProfileStrengthSidebar
import ProfileStrengthSidebar, { useProfileStrength, calculateProfileStrength } from '@/components/client-profile/ProfileStrengthSidebar';
// THE canonical ACME profile. Every field value, the summary card and the completion state
// on this page derive from this module — no page-local values.
import { getAcmeOrgProfile, mergeProfileResponse } from '@/fixtures/acmeOrgProfile';
// Demo NPI registry: the number is the input, the organization name is resolved read-only.
import { resolveNpi } from '@/services/npiRegistryDemo';
import { markPersonalizationChanged } from '@/prototype/hooks/useEvaluationRun';

import { API_BASE, apiFetch, devNote } from '../config/apiBase';
// ============================================================
// EA FOCUS BANNER - SIMPLIFIED (No breadcrumb per ChatGPT directive)
// ============================================================
function EAFocusBanner({ returnTo, restoreScrollY, navigate }) {
  const handleBackClick = () => {
    navigate(returnTo, {
      state: { restoreScrollY }
    });
  };

  return (
    <div className="bg-[#18365E] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          {/* Left: Back button */}
          <button
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Personalize
          </button>

          {/* Right: Helper text (no breadcrumb) */}
          <p className="hidden sm:block text-xs text-white/70 max-w-sm text-right">
            Completing your organization profile improves recommendation relevance.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// COLLAPSIBLE SECTION CARD
// ============================================================
function CollapsibleSection({ 
  icon: Icon, 
  title, 
  helperText,
  defaultExpanded = false, 
  hasAttention = false,
  sectionId,
  children,
  sectionRefs,
  forceExpanded = false
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || hasAttention || forceExpanded);

  // Register ref for this section
  const sectionRef = useRef(null);
  useEffect(() => {
    if (sectionRefs && sectionId) {
      sectionRefs.current[sectionId] = sectionRef;
    }
  }, [sectionRefs, sectionId]);

  // Auto-expand if has attention (missing required fields) or forceExpanded
  useEffect(() => {
    if (hasAttention || forceExpanded) {
      setIsExpanded(true);
    }
  }, [hasAttention, forceExpanded]);

  return (
    <div 
      ref={sectionRef}
      className={`bg-white rounded-xl shadow-lg border transition-all ${
        hasAttention ? 'border-[#FF6E4C]/50 ring-1 ring-[#FF6E4C]/20' : 'border-gray-200/50'
      }`}
    >
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-[#18365E]" />}
          <div className="text-left">
            <h3 className="text-lg font-semibold text-[#18365E]">{title}</h3>
            {helperText && (
              <p className="text-xs text-gray-500 mt-0.5">{helperText}</p>
            )}
          </div>
          {hasAttention && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-[#FF6E4C]/10 text-[#FF6E4C] rounded-full">
              Needs attention
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isExpanded && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================
// PROGRESS BAR - BRAND ORANGE (kept for potential other uses)
// ============================================================
function OrangeProgressBar({ percent, label }) {
  return (
    <div>
      {label && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm font-bold text-[#FF6E4C]">{percent}%</span>
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
        <div 
          className="h-full bg-[#FF6E4C] rounded-full transition-all duration-500"
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </div>
    </div>
  );
}


// The demo seed IS the canonical profile — see src/fixtures/acmeOrgProfile.
//
// This used to be a page-local object with its own values (1,200 beds, Boston MA, a different
// contact) and an `organization_type` that was not in ORGANIZATION_TYPES, so the type select
// rendered EMPTY and several sections stayed blank. Both problems are gone: there is one seed,
// and it fills every section including the collapsed ones.
const DEMO_PROFILE_FALLBACK = getAcmeOrgProfile();

const ORGANIZATION_TYPES = [
  'Health System',
  'Hospital',
  'Collaboration',
  'Healthcare Consultant',
  'Digital Health Vendor'
];

// Field → Section mapping for "missing fields" navigation
const FIELD_SECTION_MAP = {
  'organization name': 'basic-info',
  'organization_name': 'basic-info',
  'organization type': 'basic-info',
  'organization_type': 'basic-info',
  'beds': 'health-system',
  'total beds': 'health-system',
  'lives under vbc agreements': 'health-system',
  'lives_under_vbc_agreements': 'health-system',
  'vbc lives': 'health-system',
  'employed providers': 'health-system',
  'employed_providers': 'health-system',
  'affiliates': 'health-system',
  'attributed lives': 'health-system',
  'attributed_lives': 'health-system',
  'operating margin': 'economics',
  'operating_margin_pct': 'economics',
  'payer mix commercial': 'economics',
  'payer_mix_commercial_pct': 'economics',
  'payer mix medicare': 'economics',
  'payer_mix_medicare_pct': 'economics',
  'payer mix medicaid': 'economics',
  'payer_mix_medicaid_pct': 'economics',
  'service lines': 'economics',
  'service_lines': 'economics',
  'vbc models': 'economics',
  'vbc_models': 'economics',
  'service area states': 'geography',
  'service_area_states': 'geography',
  'rural': 'geography',
  'rural_pct': 'geography',
  'urban': 'geography',
  'urban_pct': 'geography',
  'suburban': 'geography',
  'suburban_pct': 'geography',
  'market competition level': 'geography',
  'market_competition_level': 'geography',
  'street': 'address',
  'city': 'address',
  'state': 'address',
  'zip': 'address',
  'contact name': 'contact',
  'contact_name': 'contact',
  'contact email': 'contact',
  'contact_email': 'contact',
  // ✅ NEW: Additional mappings for Profile Strength fields
  'annual revenue': 'economics',
  'annual_revenue': 'economics',
  'ehr vendor': 'basic-info',
  'ehr_vendor': 'basic-info',
};

// Field name → Input ref key mapping
const FIELD_INPUT_MAP = {
  'organization name': 'organization_name',
  'organization_name': 'organization_name',
  'beds': 'beds',
  'total beds': 'beds',
  'lives under vbc agreements': 'lives_under_vbc_agreements',
  'lives_under_vbc_agreements': 'lives_under_vbc_agreements',
  'vbc lives': 'lives_under_vbc_agreements',
  'market competition level': 'market_competition_level',
  'market_competition_level': 'market_competition_level',
};

// Validation helpers
const validateNPI = (npi) => /^\d{10}$/.test(npi);
const validateEmail = (email) => /^\S+@\S+\.\S+$/.test(email);
const clampPercent = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return Math.max(0, Math.min(100, num));
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ClientProfilePage = ({ 
  clientId: propClientId = null,
  embedded = false,
  onSaveSuccess = null,
  onCancelEdit = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // ============================================================
  // EA FOCUS MODE DETECTION
  // ============================================================
  const locationState = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const isEA = !!locationState.fromEA || searchParams.get('fromEA') === 'true';
  const returnTo = locationState.returnTo || '/home';
  const restoreScrollY = locationState.restoreScrollY || 0;

  // ============================================================
  // AUTH & CLIENT ID
  // ============================================================
  const { session, sessionLoading } = useAuth();
  const sessionClientId = session?.client_id || null;
  const clientId = propClientId || sessionClientId;

  // ✅ NEW: Fetch profile strength using the hook
  const { strength, loading: strengthLoading } = useProfileStrength(clientId);

  // ============================================================
  // REFS FOR SECTION NAVIGATION
  // ============================================================
  const sectionRefs = useRef({});
  const inputRefs = useRef({});

  // ============================================================
  // STATE
  // ============================================================
  const [profile, setProfile] = useState({
    organization_name: '',
    organization_type: '',
    address: { street: '', city: '', state: '', zip: '' },
    primary_contact: { name: '', title: '', email: '', phone: '' },
    organizational_npis: [],
    beds: null,
    lives_under_vbc_agreements: null,
    employed_providers: null,
    affiliates: null,
    attributed_lives: null,
    operating_margin_pct: null,
    payer_mix_commercial_pct: null,
    payer_mix_medicare_pct: null,
    payer_mix_medicaid_pct: null,
    service_lines: [],
    vbc_models: [],
    service_area_states: [],
    service_area_msas: [],
    service_area_counties: [],
    rural_pct: null,
    urban_pct: null,
    suburban_pct: null,
    market_competition_level: '',
    key_competitors: [],
    referral_regions: [],
    network_affiliations: [],
    medicaid_expansion_state: false,
    state_policy_notes: '',
    sdoh_signals: [],
    cross_border_flows: [],
    member_orgs: [],
    member_geographies: [],
    multi_state: false,
    data_rights_model: '',
    revenue_share_model: '',
    joint_objectives: [],
    exchange_protocols: [],
    consent_framework: '',
    data_policy_variants: [],
    firm_size: '',
    focus_verticals: [],
    offering_menu: [],
    delivery_model: '',
    ip_assets: [],
    average_client_size: '',
    solution_categories: [],
    care_settings: [],
    native_data_types: [],
    api_capabilities: [],
    fhir_support: false,
    target_segments: [],
    partnerships: [],
    // Seed the canonical ACME values over the empty scaffold, so the form is populated on
    // first paint rather than flashing blank until fetchProfile resolves (and staying blank
    // on the static build). A live backend response still overlays this in fetchProfile.
    ...DEMO_PROFILE_FALLBACK,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [npiValidation, setNpiValidation] = useState({});
  // Registry-resolved organisation names, keyed by NPI number (not by row index, so reordering
  // or removing a row can never show one NPI's name against another's number).
  const [npiLookup, setNpiLookup] = useState({});
  const [addressValidation, setAddressValidation] = useState({ valid: null, info: '', busy: false });
  const [overrideExisting, setOverrideExisting] = useState(false);
  const [applyingDefaults, setApplyingDefaults] = useState(false);
  const [highlightedField, setHighlightedField] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // ============================================================
  // CALCULATE COMPLETION (kept for backward compatibility)
  // ============================================================
  // RETIRED: a fourth completeness calculator ("kept for backward compatibility") that scored
  // 2-4 fields and was computed into an unused `completion` variable — nothing rendered it.
  // Strength has ONE derivation on this page (`effectiveStrength`). Removed rather than left
  // dead, for the same reason as the `readiness` fetch below.

  // ============================================================
  // DATA FETCHING
  // ============================================================
  const fetchProfile = useCallback(async () => {
    if (!clientId) return;
    try {
      const token = localStorage.getItem('dva_token');
      const response = await apiFetch(`${API_BASE}/api/v2/client-profile/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Null-safe merge, NOT a spread: a sparse backend row must not blank seeded fields.
        // See mergeProfileResponse for the failure this fixes.
        if (data.success && data.data) setProfile(p => mergeProfileResponse(p, data.data));
      } else {
        setProfile(p => ({ ...p, ...DEMO_PROFILE_FALLBACK }));
      }
    } catch (err) {
      // No backend (static demo) → seed a demo profile so the form isn't blank; no error banner.
      console.warn('Profile fetch unavailable; using demo seed:', err);
      setProfile(p => ({ ...p, ...DEMO_PROFILE_FALLBACK }));
    }
  }, [clientId]);

  // RETIRED: the `readiness` fetch was the SECOND completeness system on this page. Its
  // `missing` list drove the "Needs attention" chips while the sidebar scored from a third
  // path, which is how a 100%-complete profile still rendered attention chips. Strength now
  // has one derivation (`effectiveStrength`), so this fetch had no reader left — leaving it
  // in place would just let the second system grow back.

  useEffect(() => {
    if (!clientId) return;
    const loadAll = async () => {
      setLoading(true);
      await fetchProfile();
      setLoading(false);
    };
    loadAll();
  }, [clientId, fetchProfile]);

  // ============================================================
  // MISSING FIELD NAVIGATION (Clickable missing fields)
  // ============================================================
  const handleMissingFieldClick = (fieldName) => {
    // Normalize field name
    const normalizedField = fieldName.toLowerCase().trim();
    
    // Map field name to section
    const sectionId = FIELD_SECTION_MAP[normalizedField] || 'basic-info';
    
    // Force expand the section
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    
    // Get section ref and scroll into view after a brief delay for expansion
    setTimeout(() => {
      const sectionRef = sectionRefs.current[sectionId];
      if (sectionRef?.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Focus the input if we have a ref
      const inputKey = FIELD_INPUT_MAP[normalizedField] || normalizedField.replace(/\s+/g, '_');
      const inputEl = inputRefs.current[inputKey];
      if (inputEl) {
        setTimeout(() => {
          inputEl.focus();
        }, 300);
      }
    }, 100);

    // Highlight the field briefly
    setHighlightedField(normalizedField);
    setTimeout(() => setHighlightedField(null), 2000);

    // Auto-start editing if not already
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  // ============================================================
  // PROFILE STRENGTH — ONE derivation for this whole page
  // ============================================================
  // Previously three things could disagree: the sidebar fell back to its own local calc, the
  // "Needs attention" chips read `readiness.missing` (a different system), and /home read the
  // shared personalization vector. Now the page computes strength ONCE from the profile it is
  // actually rendering, and the API result is preferred only when it exists. The sidebar and
  // the section chips both consume this single object.
  // PRECEDENCE: the LOCAL calculation wins, always. It scores the profile object this page is
  // actually rendering, so the number can never disagree with the fields on screen.
  //
  // The server's /strength endpoint is deliberately DEMOTED to a dev-only console note. It
  // used to take precedence, and on a machine running the backend it returned its own verdict
  // from its own scale — score 64, label "Fair", and three "missing" fields that the form was
  // visibly displaying as filled. A backend must never again contradict the visible form.
  const effectiveStrength = useMemo(
    () => calculateProfileStrength(profile),
    [profile],
  );

  useEffect(() => {
    if (!strength) return;
    devNote(
      `[profile] Server /strength reported ${strength.score}% "${strength.label}" ` +
      `(missing: ${(strength.missingFields || []).join(', ') || 'none'}). ` +
      `Ignored for display — the page renders the local calculation ` +
      `(${effectiveStrength.score}% "${effectiveStrength.label}") so the score always matches the form.`,
    );
  }, [strength, effectiveStrength]);

  const isProfileComplete = (effectiveStrength?.score ?? 0) >= 100;

  // Determine which sections need attention (have missing required fields)
  const getSectionsWithAttention = useCallback(() => {
    const sections = new Set();
    // Single source: the page's one strength derivation. `readiness.missing` is NOT consulted
    // here any more — that was the second system that kept the chips lit at 100%.
    const missingFields = effectiveStrength?.missingFields || [];
    missingFields.forEach(fieldName => {
      const normalizedField = fieldName.toLowerCase().trim();
      const sectionId = FIELD_SECTION_MAP[normalizedField];
      if (sectionId) sections.add(sectionId);
    });
    return sections;
  }, [effectiveStrength]);

  const sectionsWithAttention = getSectionsWithAttention();

  // ============================================================
  // FIELD HANDLERS
  // ============================================================
  const handleFieldChange = async (field, value) => {
    setProfile(p => ({ ...p, [field]: value }));
    if (field === 'organization_type' && value && value !== profile.organization_type) {
      await applyDefaults(value, false);
    }
  };

  const handleAddressChange = (field, value) => {
    setProfile(p => ({ ...p, address: { ...p.address, [field]: value } }));
  };

  const handleContactChange = (field, value) => {
    setProfile(p => ({ ...p, primary_contact: { ...p.primary_contact, [field]: value } }));
  };

  const handleArrayAdd = (field, newValue = '') => {
    setProfile(p => ({ ...p, [field]: [...(p[field] || []), newValue] }));
  };

  const handleArrayUpdate = (field, index, value) => {
    const newArray = [...(profile[field] || [])];
    newArray[index] = value;
    setProfile(p => ({ ...p, [field]: newArray }));
  };

  const handleArrayRemove = (field, index) => {
    setProfile(p => ({ ...p, [field]: (p[field] || []).filter((_, i) => i !== index) }));
  };

  // ============================================================
  // NPI REGISTRY RESOLVE
  // ============================================================
  // The organisation name is a property of the NPI in the registry, not a field the user
  // types. Whenever the set of NPI numbers changes, resolve any we haven't seen yet and
  // cache the result by number.
  // BUG FIXED HERE — the previous version never resolved anything.
  //
  // It listed `npiLookup` in its dependencies AND used a `cancelled` cleanup flag. Marking the
  // NPIs "resolving" changed npiLookup, which re-ran the effect, which first ran the previous
  // cleanup and set cancelled = true. The lookups then completed, saw cancelled, and threw
  // their results away — so every name sat on "Resolving…" forever.
  //
  // Fix: read the cache through the state-updater form (so the effect does not depend on it)
  // and key the whole thing off the NPI numbers alone. `resolvingRef` stops the same number
  // being looked up twice while a lookup is in flight, which is what the cache-in-deps was
  // clumsily trying to do.
  // `handledRef` is a synchronous per-number guard, so a number is looked up exactly once even
  // if the effect re-runs. There is deliberately NO `cancelled` flag: results are keyed by NPI
  // number and are idempotent, so a late arrival is always safe to apply — and a cancellation
  // flag combined with a once-only guard is precisely how the old version stranded every row
  // on "Resolving…" (guard says handled, cleanup says discard, nothing ever lands).
  const handledNpisRef = useRef(new Set());
  const npiKey = (profile.organizational_npis || [])
    .map((n) => (n?.npi || '').trim())
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    const npis = npiKey ? npiKey.split(',') : [];
    npis.forEach((n) => {
      if (handledNpisRef.current.has(n)) return;
      handledNpisRef.current.add(n);
      setNpiLookup((prev) => (prev[n] ? prev : { ...prev, [n]: { status: 'resolving' } }));
      resolveNpi(n).then((r) => setNpiLookup((prev) => ({ ...prev, [n]: r })));
    });
  }, [npiKey]);

  const addNPI = () => {
    setProfile(p => ({ ...p, organizational_npis: [...(p.organizational_npis || []), { npi: '', name: '' }] }));
  };

  const updateNPI = (index, field, value) => {
    const newNPIs = [...profile.organizational_npis];
    newNPIs[index][field] = value;
    setProfile(p => ({ ...p, organizational_npis: newNPIs }));
    
    if (field === 'npi' && value && !validateNPI(value)) {
      setFieldErrors(e => ({ ...e, [`npi_${index}`]: 'Must be 10 digits' }));
    } else {
      setFieldErrors(e => {
        const { [`npi_${index}`]: removed, ...rest } = e;
        return rest;
      });
    }
  };

  const removeNPI = (index) => {
    setProfile(p => ({ ...p, organizational_npis: p.organizational_npis.filter((_, i) => i !== index) }));
  };

  const handleNpiBlur = async (index, npiValue) => {
    if (!npiValue || npiValue.length !== 10) return;
    setNpiValidation(prev => ({ ...prev, [index]: { valid: null, info: '', busy: true } }));
    
    try {
      const result = await validateNpiAPI(npiValue);
      setNpiValidation(prev => ({
        ...prev,
        [index]: {
          valid: result.valid,
          info: result.valid ? `Validated: ${result.name || 'Organization'}` : 'NPI not found',
          busy: false
        }
      }));
    } catch (err) {
      setNpiValidation(prev => ({
        ...prev,
        [index]: { valid: false, info: 'Validation unavailable', busy: false }
      }));
    }
  };

  const handleAddressBlur = async () => {
    const { street, city, state, zip } = profile.address;
    if (!street || !city || !state) return;
    
    setAddressValidation({ valid: null, info: '', busy: true });
    
    try {
      const result = await validateAddressAPI(`${street}, ${city} ${state} ${zip}`.trim());
      if (result.valid) {
        setAddressValidation({
          valid: true,
          info: `Validated. Suggested: ${result.normalized || `${result.street}, ${result.city} ${result.state} ${result.zip}`}`,
          normalized: result,
          busy: false
        });
      } else {
        setAddressValidation({ valid: false, info: 'Could not validate address.', busy: false });
      }
    } catch (err) {
      setAddressValidation({ valid: false, info: 'Validation unavailable', busy: false });
    }
  };

  const acceptNormalizedAddress = () => {
    if (addressValidation.normalized) {
      setProfile(p => ({
        ...p,
        address: {
          street: addressValidation.normalized.street || p.address.street,
          city: addressValidation.normalized.city || p.address.city,
          state: addressValidation.normalized.state || p.address.state,
          zip: addressValidation.normalized.zip || p.address.zip
        }
      }));
      setAddressValidation({ valid: true, info: 'Address updated', busy: false });
      setTimeout(() => setAddressValidation(prev => ({ ...prev, info: '' })), 3000);
    }
  };

  const applyDefaults = async (orgType, override = false) => {
    if (!orgType) return;
    try {
      setApplyingDefaults(true);
      const defaults = await getOrgDefaults(orgType);
      
      setProfile(prev => {
        const updated = { ...prev };
        for (const [key, value] of Object.entries(defaults)) {
          if (override) {
            updated[key] = value;
          } else {
            const current = updated[key];
            const isEmpty = current == null || current === '' ||
              (Array.isArray(current) && current.length === 0) ||
              (typeof current === 'object' && current !== null && Object.keys(current).length === 0);
            if (isEmpty) updated[key] = value;
          }
        }
        return updated;
      });
      
      setSuccess(`Applied ${Object.keys(defaults).length} defaults for ${orgType}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to load defaults: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setApplyingDefaults(false);
    }
  };

  const handleEmailChange = (field, value) => {
    handleContactChange(field, value);
    if (value && !validateEmail(value)) {
      setFieldErrors(e => ({ ...e, email: 'Invalid email format' }));
    } else {
      setFieldErrors(e => {
        const { email, ...rest } = e;
        return rest;
      });
    }
  };

  // ============================================================
  // SAVE
  // ============================================================
  const saveProfile = async () => {
    if (!clientId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = localStorage.getItem('dva_token');
      const response = await apiFetch(`${API_BASE}/api/v2/client-profile/${clientId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(profile)
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccess('Profile saved successfully');
        markPersonalizationChanged(); // profile edits change the inputs Evaluate runs on
        setTimeout(() => setSuccess(null), 3000);
        setIsEditing(false);
        if (onSaveSuccess) onSaveSuccess();
      } else {
        setError(data.error || 'Failed to save profile');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    fetchProfile(); // Reload to discard unsaved changes
    if (onCancelEdit) onCancelEdit();
  };

  const showConditionalFields = ['Health System', 'Hospital'].includes(profile.organization_type);

  // ============================================================
  // LOADING STATES
  // ============================================================
  if (sessionLoading && !propClientId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-[#18365E] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading your session...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#18365E] mb-2">No Organization Selected</h2>
            <p className="text-gray-600 mb-6">
              Your account is not currently associated with an organization.
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-[#18365E] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* EA FOCUS BANNER: Show when navigated from EA Personalization */}
      {isEA && !embedded && (
        <EAFocusBanner
          returnTo={returnTo}
          restoreScrollY={restoreScrollY}
          navigate={navigate}
        />
      )}

      {/* Status Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-md flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {/* Hidden dummy fields to confuse Chrome's address detection */}
        <input type="text" name="prevent_autofill" style={{ display: 'none' }} autoComplete="off" />
        <input type="text" name="prevent_autofill_2" style={{ display: 'none' }} autoComplete="address-level1" />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header with Edit button (NO TABS - per MVP directive) */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#18365E]">Organization Profile</h1>
              {/* Sub-header reflects the real state — a complete profile is not asked to complete itself. */}
              <p className="text-sm text-gray-500 mt-1">
                {isProfileComplete
                  ? 'Your profile is complete. These values personalize every score and recommendation.'
                  : 'Complete your profile to receive more accurate, personalized recommendations.'}
              </p>
            </div>

            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <ActionButton onClick={handleCancelEdit} icon={X} text="Cancel" className="bg-gray-200 text-gray-700 hover:bg-gray-300" />
                  <ActionButton onClick={saveProfile} disabled={saving} icon={saving ? RefreshCw : Save} text={saving ? 'Saving...' : 'Save Changes'} className="bg-[#18365E] text-white hover:bg-[#18365E]/90" spin={saving} />
                </>
              ) : (
                <ActionButton onClick={() => setIsEditing(true)} icon={Edit2} text={isProfileComplete ? 'Review Profile' : 'Continue Completing Profile'} className="bg-[#18365E] text-white hover:bg-[#18365E]/90" />
              )}
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Profile Sections */}
            <div className="lg:col-span-2 space-y-4">
              
              {/* SECTION: Basic Information - ALWAYS EXPANDED */}
              <CollapsibleSection
                icon={Building2}
                title="Basic Information"
                defaultExpanded={true}
                sectionId="basic-info"
                sectionRefs={sectionRefs}
              >
                <div className="space-y-4">
                  <InputField
                    label="Organization Name *"
                    type="text"
                    value={profile.organization_name || ''}
                    onChange={(e) => handleFieldChange('organization_name', e.target.value)}
                    placeholder="Enter organization name"
                    disabled={!isEditing}
                    highlighted={highlightedField === 'organization name' || highlightedField === 'organization_name'}
                    inputRef={(el) => { inputRefs.current['organization_name'] = el; }}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization Type *</label>
                    <select
                      value={profile.organization_type || ''}
                      onChange={(e) => handleFieldChange('organization_type', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-[#FF6E4C]/50 focus:border-[#FF6E4C] disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      <option value="">Select type...</option>
                      {ORGANIZATION_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {profile.organization_type && isEditing && (
                      <div className="mt-3 space-y-2">
                        <button
                          type="button"
                          onClick={() => applyDefaults(profile.organization_type, overrideExisting)}
                          disabled={applyingDefaults}
                          className="inline-flex items-center gap-2 text-sm text-[#FF6E4C] hover:text-[#FF6E4C]/80 font-medium disabled:opacity-50"
                        >
                          <RefreshCw className={`w-4 h-4 ${applyingDefaults ? 'animate-spin' : ''}`} />
                          {applyingDefaults ? 'Loading defaults...' : 'Load Defaults'}
                        </button>
                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={overrideExisting}
                            onChange={(e) => setOverrideExisting(e.target.checked)}
                            className="rounded border-gray-300 text-[#FF6E4C] focus:ring-[#FF6E4C]"
                          />
                          <span>Override existing values</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </CollapsibleSection>

              {/* SECTION: Health System Footprint - EXPANDED by default for Health System/Hospital */}
              {showConditionalFields && (
                <CollapsibleSection
                  icon={Activity}
                  title="Health System Footprint"
                  helperText="Used to estimate scale, complexity, and achievable ROI."
                  defaultExpanded={true}
                  hasAttention={sectionsWithAttention.has('health-system')}
                  sectionId="health-system"
                  sectionRefs={sectionRefs}
                  forceExpanded={expandedSections['health-system']}
                >
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <InputField 
                        label="Total Beds *" 
                        type="number" 
                        value={profile.beds || ''} 
                        onChange={(e) => handleFieldChange('beds', parseInt(e.target.value) || null)} 
                        placeholder="e.g., 500" 
                        disabled={!isEditing}
                        highlighted={highlightedField === 'beds' || highlightedField === 'total beds'}
                        inputRef={(el) => { inputRefs.current['beds'] = el; }}
                      />
                      <InputField 
                        label="Lives Under VBC Agreements *" 
                        type="number" 
                        value={profile.lives_under_vbc_agreements || ''} 
                        onChange={(e) => handleFieldChange('lives_under_vbc_agreements', parseInt(e.target.value) || null)} 
                        placeholder="e.g., 25000" 
                        disabled={!isEditing}
                        highlighted={highlightedField === 'lives under vbc agreements' || highlightedField === 'vbc lives'}
                        inputRef={(el) => { inputRefs.current['lives_under_vbc_agreements'] = el; }}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <InputField label="Employed Providers" type="number" value={profile.employed_providers || ''} onChange={(e) => handleFieldChange('employed_providers', parseInt(e.target.value) || null)} placeholder="e.g., 1200" disabled={!isEditing} />
                      <InputField label="Affiliates" type="number" value={profile.affiliates || ''} onChange={(e) => handleFieldChange('affiliates', parseInt(e.target.value) || null)} placeholder="e.g., 15" disabled={!isEditing} />
                      <InputField label="Attributed Lives" type="number" value={profile.attributed_lives || ''} onChange={(e) => handleFieldChange('attributed_lives', parseInt(e.target.value) || null)} placeholder="e.g., 50000" disabled={!isEditing} />
                    </div>
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION: Economics & Payer Mix - COLLAPSED by default */}
              {showConditionalFields && (
                <CollapsibleSection
                  icon={TrendingUp}
                  title="Economics & Payer Mix"
                  helperText="Directly impacts financial assumptions and payback modeling."
                  defaultExpanded={false}
                  hasAttention={sectionsWithAttention.has('economics')}
                  sectionId="economics"
                  sectionRefs={sectionRefs}
                  forceExpanded={expandedSections['economics']}
                >
                  <div className="space-y-4">
                    {/* Annual Revenue is a CRITICAL field in the strength calculator. It had no
                        input here, so the requirement was unsatisfiable on screen — a trap for
                        anyone trying to reach 100% by filling the form. */}
                    <InputField label="Annual Revenue ($)" type="number" step="1000000" value={profile.annual_revenue || ''} onChange={(e) => handleFieldChange('annual_revenue', parseInt(e.target.value, 10) || null)} placeholder="e.g., 3200000000" disabled={!isEditing} inputRef={(el) => { inputRefs.current['annual_revenue'] = el; }} highlighted={highlightedField === 'annual revenue' || highlightedField === 'annual_revenue'} />
                    <InputField label="Operating Margin (%)" type="number" step="0.1" value={profile.operating_margin_pct || ''} onChange={(e) => handleFieldChange('operating_margin_pct', clampPercent(e.target.value))} placeholder="e.g., 3.5" disabled={!isEditing} />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <InputField label="Commercial (%)" type="number" value={profile.payer_mix_commercial_pct || ''} onChange={(e) => handleFieldChange('payer_mix_commercial_pct', clampPercent(e.target.value))} placeholder="e.g., 45" disabled={!isEditing} />
                      <InputField label="Medicare (%)" type="number" value={profile.payer_mix_medicare_pct || ''} onChange={(e) => handleFieldChange('payer_mix_medicare_pct', clampPercent(e.target.value))} placeholder="e.g., 35" disabled={!isEditing} />
                      <InputField label="Medicaid (%)" type="number" value={profile.payer_mix_medicaid_pct || ''} onChange={(e) => handleFieldChange('payer_mix_medicaid_pct', clampPercent(e.target.value))} placeholder="e.g., 20" disabled={!isEditing} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service Lines</label>
                      <ArrayInput values={profile.service_lines || []} onAdd={() => handleArrayAdd('service_lines')} onUpdate={(idx, val) => handleArrayUpdate('service_lines', idx, val)} onRemove={(idx) => handleArrayRemove('service_lines', idx)} disabled={!isEditing} placeholder="e.g., Cardiology" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">VBC Models</label>
                      <ArrayInput values={profile.vbc_models || []} onAdd={() => handleArrayAdd('vbc_models')} onUpdate={(idx, val) => handleArrayUpdate('vbc_models', idx, val)} onRemove={(idx) => handleArrayRemove('vbc_models', idx)} disabled={!isEditing} placeholder="e.g., ACO" />
                    </div>
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION: Geography & Policy - COLLAPSED by default */}
              <CollapsibleSection
                icon={Globe}
                title="Geography & Policy"
                helperText="Influences regulatory constraints and reimbursement dynamics."
                defaultExpanded={false}
                hasAttention={sectionsWithAttention.has('geography')}
                sectionId="geography"
                sectionRefs={sectionRefs}
                forceExpanded={expandedSections['geography']}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Area States</label>
                    <ArrayInput values={profile.service_area_states || []} onAdd={() => handleArrayAdd('service_area_states')} onUpdate={(idx, val) => handleArrayUpdate('service_area_states', idx, val)} onRemove={(idx) => handleArrayRemove('service_area_states', idx)} disabled={!isEditing} placeholder="e.g., PA, OH" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <InputField label="Rural (%)" type="number" value={profile.rural_pct || ''} onChange={(e) => handleFieldChange('rural_pct', clampPercent(e.target.value))} placeholder="e.g., 30" disabled={!isEditing} />
                    <InputField label="Urban (%)" type="number" value={profile.urban_pct || ''} onChange={(e) => handleFieldChange('urban_pct', clampPercent(e.target.value))} placeholder="e.g., 50" disabled={!isEditing} />
                    <InputField label="Suburban (%)" type="number" value={profile.suburban_pct || ''} onChange={(e) => handleFieldChange('suburban_pct', clampPercent(e.target.value))} placeholder="e.g., 20" disabled={!isEditing} />
                  </div>
                  <InputField 
                    label="Market Competition Level" 
                    type="text" 
                    value={profile.market_competition_level || ''} 
                    onChange={(e) => handleFieldChange('market_competition_level', e.target.value)} 
                    placeholder="e.g., High, Moderate, Low" 
                    disabled={!isEditing}
                    highlighted={highlightedField === 'market competition level' || highlightedField === 'market_competition_level'}
                    inputRef={(el) => { inputRefs.current['market_competition_level'] = el; }}
                  />
                </div>
              </CollapsibleSection>

              {/* SECTION: Address - COLLAPSED by default */}
              <CollapsibleSection
                icon={MapPin}
                title="Address"
                defaultExpanded={false}
                hasAttention={sectionsWithAttention.has('address')}
                sectionId="address"
                sectionRefs={sectionRefs}
                forceExpanded={expandedSections['address']}
              >
                <div className="space-y-4">
                  <InputField label="Street" type="text" value={profile.address?.street || ''} onChange={(e) => handleAddressChange('street', e.target.value)} onBlur={handleAddressBlur} placeholder="123 Main St" disabled={!isEditing} />
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="City" type="text" value={profile.address?.city || ''} onChange={(e) => handleAddressChange('city', e.target.value)} onBlur={handleAddressBlur} placeholder="Philadelphia" disabled={!isEditing} />
                    <InputField label="State" type="text" value={profile.address?.state || ''} onChange={(e) => handleAddressChange('state', e.target.value)} onBlur={handleAddressBlur} placeholder="PA" disabled={!isEditing} />
                  </div>
                  <InputField label="ZIP Code" type="text" value={profile.address?.zip || ''} onChange={(e) => handleAddressChange('zip', e.target.value)} onBlur={handleAddressBlur} placeholder="19103" disabled={!isEditing} />
                  {addressValidation.busy && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Validating address...</span>
                    </div>
                  )}
                  {!addressValidation.busy && addressValidation.info && (
                    <div className={`p-3 rounded-lg ${addressValidation.valid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                      <p className={`text-sm ${addressValidation.valid ? 'text-green-700' : 'text-yellow-700'}`}>{addressValidation.info}</p>
                      {addressValidation.valid && addressValidation.normalized && isEditing && (
                        <button type="button" onClick={acceptNormalizedAddress} className="mt-2 text-sm text-green-700 underline hover:text-green-800">Use suggested address</button>
                      )}
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              {/* SECTION: Primary Contact - COLLAPSED by default */}
              <CollapsibleSection
                icon={Users}
                title="Primary Contact"
                defaultExpanded={false}
                hasAttention={sectionsWithAttention.has('contact')}
                sectionId="contact"
                sectionRefs={sectionRefs}
                forceExpanded={expandedSections['contact']}
              >
                <div className="space-y-4">
                  <InputField label="Name" type="text" value={profile.primary_contact?.name || ''} onChange={(e) => handleContactChange('name', e.target.value)} placeholder="John Doe" disabled={!isEditing} />
                  <InputField label="Title" type="text" value={profile.primary_contact?.title || ''} onChange={(e) => handleContactChange('title', e.target.value)} placeholder="Chief Data Officer" disabled={!isEditing} />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <InputField label="Email" type="email" value={profile.primary_contact?.email || ''} onChange={(e) => handleEmailChange('email', e.target.value)} placeholder="john.doe@example.com" disabled={!isEditing} />
                      {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
                    </div>
                    <InputField label="Phone" type="tel" value={profile.primary_contact?.phone || ''} onChange={(e) => handleContactChange('phone', e.target.value)} placeholder="(555) 123-4567" disabled={!isEditing} />
                  </div>
                </div>
              </CollapsibleSection>

              {/* SECTION: Organizational NPIs - COLLAPSED by default */}
              <CollapsibleSection
                icon={Hash}
                title="Organizational NPIs"
                defaultExpanded={false}
                sectionId="npis"
                sectionRefs={sectionRefs}
                forceExpanded={expandedSections['npis']}
              >
                <div className="space-y-3">
                  {profile.organizational_npis?.map((npi, index) => (
                    <div key={index} className="flex gap-2">
                      <div className="flex-1 space-y-2">
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-1">NPI Number</label>
                          <div className="relative">
                            <input 
                              type="text" 
                              value={npi.npi || ''} 
                              onChange={(e) => updateNPI(index, 'npi', e.target.value)} 
                              onBlur={(e) => handleNpiBlur(index, e.target.value)} 
                              disabled={!isEditing || npiValidation[index]?.busy} 
                              autoComplete="new-password"
                              data-form-type="other"
                              data-lpignore="true"
                              className={`w-full px-3 py-2 pr-10 border rounded-lg disabled:bg-gray-50 ${npiValidation[index]?.valid === true ? 'border-green-500' : npiValidation[index]?.valid === false ? 'border-red-500' : 'border-gray-300'}`} 
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              {npiValidation[index]?.busy && <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />}
                              {!npiValidation[index]?.busy && npiValidation[index]?.valid === true && <Check className="w-4 h-4 text-green-500" />}
                              {!npiValidation[index]?.busy && npiValidation[index]?.valid === false && <AlertCircle className="w-4 h-4 text-red-500" />}
                            </div>
                          </div>
                          {npiValidation[index]?.info && <p className={`text-xs mt-1 ${npiValidation[index]?.valid ? 'text-green-600' : 'text-red-600'}`}>{npiValidation[index]?.info}</p>}
                        </div>
                        {/* Organization name is RESOLVED from the registry, never typed. A
                            hand-typed name beside a real NPI would look authoritative and be
                            wrong. See src/services/npiRegistryDemo.ts. */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                          {(() => {
                            const lookup = npiLookup[(npi.npi || '').trim()];
                            const status = lookup?.status ?? 'idle';
                            return (
                              <>
                                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 flex items-center gap-2 min-h-[42px]">
                                  {status === 'resolving' && (
                                    <><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /><span className="text-gray-500">Resolving…</span></>
                                  )}
                                  {status === 'resolved' && (
                                    <><Check className="w-4 h-4 text-green-500 shrink-0" /><span>{lookup.record.organizationName}</span></>
                                  )}
                                  {status === 'not_found' && (
                                    <><AlertCircle className="w-4 h-4 text-amber-500 shrink-0" /><span className="text-gray-500">Not found in registry</span></>
                                  )}
                                  {status === 'invalid' && (
                                    <><AlertCircle className="w-4 h-4 text-amber-500 shrink-0" /><span className="text-gray-500">{lookup.message}</span></>
                                  )}
                                  {status === 'idle' && <span className="text-gray-400">Enter an NPI to resolve</span>}
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1">Registry lookup</p>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      {isEditing && (
                        <button type="button" onClick={() => removeNPI(index)} className="self-start mt-7 p-2 text-red-600 hover:bg-red-50 rounded">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {isEditing && (
                    <button type="button" onClick={addNPI} className="w-full py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-[#FF6E4C] hover:text-[#FF6E4C] transition">+ Add NPI</button>
                  )}
                  {!isEditing && (!profile.organizational_npis || profile.organizational_npis.length === 0) && (
                    <p className="text-sm text-gray-500 italic">No NPIs added yet.</p>
                  )}
                </div>
              </CollapsibleSection>
            </div>

            {/* ============================================================ */}
            {/* RIGHT COLUMN: UNIFIED PROFILE STRENGTH SIDEBAR */}
            {/* Replaces the old two-card (Readiness + Status) sidebar */}
            {/* ============================================================ */}
            <div className="lg:col-span-1">
              <ProfileStrengthSidebar
                profile={profile}
                strength={effectiveStrength}
                isLoading={strengthLoading || loading}
                onMissingFieldClick={handleMissingFieldClick}
              />
            </div>
          </div>
        </main>
      </form>
    </div>
  );
};

// ============================================================
// HELPER COMPONENTS
// ============================================================
const InfoRow = ({ label, value }) => (
  <div>
    <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
    <p className="text-sm font-medium text-gray-900 mt-0.5">{value || 'Not provided'}</p>
  </div>
);

const ArrayInput = ({ values, onAdd, onUpdate, onRemove, disabled, placeholder }) => (
  <div className="space-y-2">
    {values.map((value, index) => (
      <div key={index} className="flex gap-2">
        <input 
          type="text" 
          value={value} 
          onChange={(e) => onUpdate(index, e.target.value)} 
          disabled={disabled} 
          placeholder={placeholder} 
          autoComplete="new-password"
          data-form-type="other"
          data-lpignore="true"
          className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm disabled:bg-gray-50" 
        />
        {!disabled && <button type="button" onClick={() => onRemove(index)} className="p-2 text-red-600 hover:bg-red-50 rounded-md"><X className="w-4 h-4" /></button>}
      </div>
    ))}
    {!disabled && <button type="button" onClick={onAdd} className="w-full py-2 border-2 border-dashed border-gray-300 rounded-md text-xs text-gray-600 hover:border-[#FF6E4C] hover:text-[#FF6E4C]">+ Add</button>}
  </div>
);

const InputField = ({ label, error, highlighted, inputRef, ...props }) => (
  <div className={`transition-all duration-300 ${highlighted ? 'ring-2 ring-[#FF6E4C] rounded-lg p-2 -m-2' : ''}`}>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input 
      {...props}
      ref={inputRef}
      autoComplete="new-password"
      data-form-type="other"
      data-lpignore="true"
      className={`w-full px-3 py-2 bg-white border rounded-md shadow-sm focus:ring-2 focus:ring-[#FF6E4C]/50 focus:border-[#FF6E4C] disabled:bg-gray-50 disabled:text-gray-500 ${error ? 'border-red-500' : 'border-gray-300'}`} 
    />
  </div>
);

const ActionButton = ({ onClick, disabled, icon, text, className, spin }) => {
  const IconComponent = icon;
  return (
    <button 
      type="button"
      onClick={onClick} 
      disabled={disabled} 
      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
    >
      {IconComponent && <IconComponent className={`w-5 h-5 ${spin ? 'animate-spin' : ''}`} />}
      {text}
    </button>
  );
};

export default ClientProfilePage;